import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft, FileDown, Download, CheckCircle2, XCircle, Loader2,
    TrendingUp, Brain, MessageSquare, Puzzle, Star,
    User, Calendar, Briefcase, Award, ChevronDown, ChevronUp,
    Volume2, FileText, BarChart3, Mic, MapPin, Phone, Mail,
    GraduationCap, Building2, Sparkles, ShieldCheck, AlertTriangle,
    Languages, BookOpen, Target, ShieldAlert, Eye,
    WifiOff, MonitorOff, Clock, Camera, Users, Activity,
    AudioLines, ScanFace, MousePointerClick, Info, Timer,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import ResponseAudioPlayer from '@/components/report/ResponseAudioPlayer';
import { groupedSkills } from '@/lib/resumeSkills';
import { ResumeDownloadButtons } from '@/components/resume/ResumeDownload';
import SkillsRadarChart from '@/components/SkillsRadarChart';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import {
    parseReportFetchResult,
    shouldStopReportPolling,
    mergeMonitoringData,
    hasMonitoringData,
    resolveIntegrityDisplay,
    formatProctoringEntry,
    formatEventTime,
} from '@/utils/reportStatus.js';

/* ── colour helpers ─────────────────────────────────────────────────────── */
function sc(n) {
    if (n >= 80) return { fg: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500', stroke: '#10b981', badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20' };
    if (n >= 60) return { fg: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500', stroke: '#f59e0b', badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' };
    return { fg: 'text-red-600 dark:text-red-400', bar: 'bg-red-500', stroke: '#ef4444', badge: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20' };
}
function grade(n) { return n >= 85 ? 'Excellent' : n >= 70 ? 'Good' : n >= 55 ? 'Average' : 'Poor'; }

/* ── count-up hook ──────────────────────────────────────────────────────── */
function useCountUp(target, dur = 900) {
    const [v, setV] = useState(0);
    const r = useRef();
    useEffect(() => {
        const end = Math.round(Number(target) || 0);
        let t0 = null;
        const step = (ts) => {
            if (!t0) t0 = ts;
            const p = Math.min((ts - t0) / dur, 1);
            setV(Math.round((1 - Math.pow(1 - p, 3)) * end));
            if (p < 1) r.current = requestAnimationFrame(step); else setV(end);
        };
        r.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(r.current);
    }, [target]);
    return v;
}

/* ── donut gauge ────────────────────────────────────────────────────────── */
function Donut({ score }) {
    const d = useCountUp(score);
    const R = 52, cx = 68, circ = 2 * Math.PI * R;
    const { stroke, fg } = sc(d);
    return (
        <div className="relative flex items-center justify-center" style={{ width: 136, height: 136 }}>
            <svg width={136} height={136} className="-rotate-90 absolute inset-0">
                <circle cx={cx} cy={cx} r={R} fill="none" strokeWidth="10" stroke="currentColor" className="text-muted/40" />
                <circle cx={cx} cy={cx} r={R} fill="none" strokeWidth="10" stroke={stroke} strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={circ - (d / 100) * circ}
                    style={{ filter: `drop-shadow(0 0 5px ${stroke}50)` }} />
            </svg>
            <div className="flex flex-col items-center z-10">
                <span className={cn('text-2xl font-black tabular-nums leading-none', fg)}>{d}%</span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-0.5">{grade(d)}</span>
            </div>
        </div>
    );
}

/* ── metric row ─────────────────────────────────────────────────────────── */
function Metric({ Icon, label, score }) {
    const d = useCountUp(score);
    const [w, setW] = useState(0);
    const { fg, bar, badge } = sc(d);
    useEffect(() => { const t = setTimeout(() => setW(Math.min(100, score || 0)), 120); return () => clearTimeout(t); }, [score]);
    return (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/50">
                <Icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold text-foreground">{label}</span>
                    <span className={cn('text-sm font-black tabular-nums', fg)}>{d}%</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full rounded-full transition-all duration-1000 ease-out', bar)} style={{ width: `${w}%` }} />
                </div>
            </div>
            <span className={cn('shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold', badge)}>{grade(d)}</span>
        </div>
    );
}

/* ── recommendation badge ───────────────────────────────────────────────── */
const RECO = {
    strong_hire: { label: 'Strong Hire', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25', dot: 'bg-emerald-500' },
    hire:        { label: 'Hire',        cls: 'bg-green-500/15  text-green-700  dark:text-green-400  border-green-500/25',   dot: 'bg-green-500' },
    maybe:       { label: 'Maybe',       cls: 'bg-amber-500/15  text-amber-700  dark:text-amber-400  border-amber-500/25',   dot: 'bg-amber-500' },
    no_hire:     { label: 'No Hire',     cls: 'bg-red-500/15    text-red-700    dark:text-red-400    border-red-500/25',     dot: 'bg-red-500'   },
};
function RecoBadge({ value }) {
    const m = RECO[value] || { label: value, cls: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground' };
    return (
        <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold', m.cls)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', m.dot)} />{m.label}
        </span>
    );
}

/* ── expandable text ────────────────────────────────────────────────────── */
function ExpandableText({ text, className, clampLines = 4, lineHeight = 1.6, fontSize = 12 }) {
    const [expanded, setExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        // Compare scrollHeight (full) vs clientHeight (visible) to decide if clamped
        setIsClamped(el.scrollHeight > el.clientHeight + 2);
    }, [text]);

    const clampClass = !expanded ? `line-clamp-${clampLines}` : '';

    return (
        <div>
            <p ref={ref} className={cn(className, clampClass)}>{text}</p>
            {(isClamped || expanded) && (
                <button
                    onClick={() => setExpanded(v => !v)}
                    className="mt-1 text-[10px] font-semibold text-primary hover:underline focus:outline-none"
                >
                    {expanded ? 'Read less ↑' : 'Read more ↓'}
                </button>
            )}
        </div>
    );
}

/* ── skill bar ──────────────────────────────────────────────────────────── */
function SkillBar({ skill, score, description }) {
    const [w, setW] = useState(0);
    const { fg, bar } = sc(score);
    useEffect(() => { const t = setTimeout(() => setW(Math.min(100, score || 0)), 80); return () => clearTimeout(t); }, [score]);
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">{skill}</span>
                <span className={cn('text-xs font-bold tabular-nums', fg)}>{(score || 0).toFixed(0)}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className={cn('h-full rounded-full transition-all duration-1000 ease-out', bar)} style={{ width: `${w}%` }} />
            </div>
            {description && <p className="text-[11px] italic leading-snug text-muted-foreground">{description}</p>}
        </div>
    );
}

/* ── question accordion ─────────────────────────────────────────────────── */
function QCard({ q, i }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
            <button type="button" onClick={() => setOpen(o => !o)}
                className="flex w-full items-start gap-3 p-3.5 text-left transition-colors hover:bg-muted/30">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary mt-0.5">{i + 1}</span>
                <p className="flex-1 text-sm font-medium text-foreground leading-snug">{q.questionText}</p>
                {q.questionFormat === 'mcq' && (
                    <span className="shrink-0 rounded-full bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-400 mt-0.5">MCQ</span>
                )}
                {open ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />}
            </button>
            {open && (
                <div className="border-t border-border bg-muted/20 px-3.5 py-3 space-y-2.5">
                    {q.questionFormat === 'mcq' ? (
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Options</p>
                            <div className="space-y-1">
                                {(q.options || []).map((opt, oi) => (
                                    <div key={oi} className={cn(
                                        'flex items-center gap-2 rounded-md px-2 py-1 text-xs',
                                        opt.isCorrect
                                            ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                                            : 'bg-background text-muted-foreground'
                                    )}>
                                        <span className="shrink-0 font-bold">{String.fromCharCode(65 + oi)}.</span>
                                        <span className="flex-1">{opt.text}</span>
                                        {opt.isCorrect && <span className="shrink-0 font-semibold">✓ correct</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Ideal Answer</p>
                            <div className="text-xs leading-relaxed text-foreground [&_strong]:font-semibold [&_strong]:text-primary [&_a]:text-primary">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{q.idealAnswer || ''}</ReactMarkdown>
                            </div>
                        </div>
                    )}
                    {q.keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {q.keywords.map(kw => (
                                <span key={kw} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">{kw}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── response card ──────────────────────────────────────────────────────── */

function ResCard({ res, i, sessionId }) {
    const [retrying, setRetrying] = useState(false);
    // Local transcript override — updated after a successful retry without needing a page reload
    const [localTranscript, setLocalTranscript] = useState(null);

    const score = res.aiScore ?? 0;
    const { fg, bar } = sc(score);
    const isMcq = res.isAutoScored || !!res.selectedOption;
    const mcqCorrect = isMcq && score >= 100;

    return (
        <div className="rounded-xl border border-border bg-card p-3.5 space-y-2.5">
            {/* Question */}
            <div className="flex items-start gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">{i + 1}</span>
                <p className="text-xs font-semibold text-foreground leading-snug">{res.questionText}</p>
            </div>

            {/* Score */}
            <div className="flex items-center gap-2">
                <span className={cn('text-sm font-black tabular-nums', fg)}>{Math.round(score)}%</span>
                {isMcq ? (
                    <span className={cn(
                        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                        mcqCorrect
                            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-500/10 text-rose-700 dark:text-rose-400'
                    )}>
                        {mcqCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                ) : (
                    <span className="text-[10px] text-muted-foreground">AI score</span>
                )}
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                <div className={cn('h-full rounded-full', bar)} style={{ width: `${Math.min(100, score)}%` }} />
            </div>

            {/* MCQ selected option */}
            {isMcq && res.selectedOption && (
                <div className={cn(
                    'rounded-lg border px-2.5 py-2 text-[11px]',
                    mcqCorrect
                        ? 'border-emerald-500/30 bg-emerald-500/5'
                        : 'border-rose-500/30 bg-rose-500/5'
                )}>
                    <span className="font-semibold text-muted-foreground">Selected: </span>
                    <span className={mcqCorrect ? 'text-emerald-800 dark:text-emerald-300' : 'text-rose-800 dark:text-rose-300'}>
                        {res.selectedOption}
                    </span>
                </div>
            )}

            {/* Transcript + retry */}
            {(() => {
                const displayText        = localTranscript ?? res.transcribedText ?? '';
                const FAILED_MARKERS     = ['[No speech detected]','[No response recorded]','[Transcription pending]'];
                const isFailedTranscript = FAILED_MARKERS.includes(displayText.trim());
                const hasAudio           = res.audioFileUrl && res.audioFileUrl !== 'N/A';
                const wasFixed           = localTranscript !== null && !isFailedTranscript;

                const handleRetry = async () => {
                    if (!sessionId || !res._id) return;
                    setRetrying(true);
                    try {
                        const result = await apiClient.retryResponseTranscription(sessionId, res._id);
                        const newTranscript = result.transcript || '';
                        setLocalTranscript(newTranscript || '[No speech detected]');
                        if (newTranscript) {
                            toast.success('Transcription successful. Score is being updated in background.');
                        } else {
                            toast.warning('Audio processed but no speech found. Check audio quality.');
                        }
                    } catch (e) {
                        toast.error(e.message || 'Retry failed. Try again shortly.');
                    } finally {
                        setRetrying(false);
                    }
                };

                if (!displayText) return null;

                return (
                    <div className="space-y-1.5">
                        {/* Transcript text */}
                        <div className={cn(
                            'rounded-lg border px-2.5 py-2 text-[11px] leading-relaxed',
                            isFailedTranscript
                                ? 'border-amber-500/30 bg-amber-500/5'
                                : wasFixed
                                    ? 'border-emerald-500/30 bg-emerald-500/5'
                                    : 'border-border bg-muted/30'
                        )}>
                            {isFailedTranscript ? (
                                /* Failed — show warning label, not the marker text */
                                <span className="font-medium text-amber-700 dark:text-amber-400">
                                    ⚠ No transcription available
                                    {hasAudio ? ' — audio exists, click below to transcribe' : ' — no audio recorded'}
                                </span>
                            ) : wasFixed ? (
                                /* Successfully re-transcribed */
                                <>
                                    <span className="font-semibold text-emerald-700 dark:text-emerald-400 not-italic">
                                        ✓ Transcribed from audio:
                                    </span>
                                    <p className="mt-1 italic text-emerald-800 dark:text-emerald-300">
                                        &ldquo;{displayText}&rdquo;
                                    </p>
                                </>
                            ) : (
                                /* Normal transcript */
                                <p className="italic text-muted-foreground">&ldquo;{displayText}&rdquo;</p>
                            )}
                        </div>

                        {/* Retry button — shown when transcript failed AND audio exists */}
                        {isFailedTranscript && hasAudio && (
                            <button
                                type="button"
                                onClick={handleRetry}
                                disabled={retrying}
                                className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
                            >
                                {retrying ? (
                                    <>
                                        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-500/30 border-t-amber-500" />
                                        Transcribing from audio…
                                    </>
                                ) : (
                                    <>↻ Transcribe from audio</>
                                )}
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Audio player */}
            {res.audioFileUrl && res.audioFileUrl !== 'N/A' && (
                <ResponseAudioPlayer
                    src={`${API_CONFIG.STATIC_BASE_URL}${res.audioFileUrl}`}
                    durationSeconds={res.audioDurationSeconds ?? null}
                    peaks={res.audioPeaks ?? null}
                    downloadName={`answer-${i + 1}.webm`}
                />
            )}
        </div>
    );
}

/* ── resume tab ─────────────────────────────────────────────────────────── */
function ResumeTab({ candidateId }) {
    const [resume, setResume] = useState(null);
    const [loading, setLoading] = useState(true);
    const [noResume, setNoResume] = useState(false);

    useEffect(() => {
        if (!candidateId) { setLoading(false); setNoResume(true); return; }
        apiClient.getResumeByCandidateId(candidateId)
            .then(r => setResume(r.data))
            .catch(() => setNoResume(true))
            .finally(() => setLoading(false));
    }, [candidateId]);

    if (loading) return (
        <div className="space-y-3 p-1">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-24 shrink-0" />
                    <Skeleton className="h-4 flex-1" style={{ maxWidth: [200, 280, 160, 240, 180][i] }} />
                </div>
            ))}
        </div>
    );
    if (noResume || !resume) return (
        <div className="flex h-44 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20">
            <FileText className="h-7 w-7 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No resume on file</p>
            <p className="text-xs text-muted-foreground">Candidate was not sourced from a resume upload.</p>
        </div>
    );

    const p = resume.parsedData || {};
    const ats = resume.atsScore || 0;
    const { fg: atsFg, bar: atsBar, badge: atsBadge } = sc(ats);
    const bd = resume.scoreBreakdown || {};
    const an = resume.resumeAnalysis || {};
    // Skills are stored as a flat list on older resumes and grouped by
    // category on newer ones; checking `p.skills.length` found nothing on the
    // grouped majority, so the card silently vanished for most candidates.
    const skillGroups = groupedSkills(p);
    const evaluated = resume.status === 'completed';

    return (
        <div className="space-y-4">
            {/* Download — typeset from the parsed data. The uploaded file is
                deleted once evaluation completes, so this is the only way to
                get a copy of most resumes, and it only exists after evaluation. */}
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3">
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground">Download resume</p>
                    <p className="text-[11px] text-muted-foreground">
                        {evaluated
                            ? 'A formatted PDF built from the evaluated resume.'
                            : 'Available once the evaluation has finished.'}
                    </p>
                </div>
                <ResumeDownloadButtons resume={resume} />
            </div>

            {/* ATS hero */}
            <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">ATS Match Score</p>
                        <div className="flex items-baseline gap-2 mt-0.5">
                            <span className={cn('text-3xl font-black tabular-nums', atsFg)}>{ats}%</span>
                            <span className={cn('rounded-full border px-2 py-0.5 text-xs font-semibold', atsBadge)}>{grade(ats)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">vs <span className="font-medium text-foreground">{resume.templateId?.title || 'job template'}</span></p>
                    </div>
                    <div className="grid grid-cols-2 gap-x-5 gap-y-1.5 min-w-[240px]">
                        {[['Keywords', bd.keywordCoverage], ['Experience', bd.experienceRelevance], ['Education', bd.educationFit], ['Skills', bd.technicalSkills]].map(([l, v]) => {
                            const val = v || 0; const { bar } = sc(val);
                            return (
                                <div key={l}>
                                    <div className="flex items-center justify-between gap-1 mb-0.5">
                                        <span className="text-[10px] text-muted-foreground">{l}</span>
                                        <span className="text-[10px] font-semibold text-foreground">{val}%</span>
                                    </div>
                                    <div className="h-1 overflow-hidden rounded-full bg-muted">
                                        <div className={cn('h-full rounded-full', bar)} style={{ width: `${val}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                {/* Left col */}
                <div className="space-y-3 lg:col-span-2">
                    {/* Profile */}
                    <div className="rounded-xl border border-border bg-card p-4 space-y-2.5">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-primary" />Profile</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {p.name && <Inf icon={User} l="Name" v={p.name} />}
                            {p.email && <Inf icon={Mail} l="Email" v={p.email} />}
                            {p.phone && <Inf icon={Phone} l="Phone" v={p.phone} />}
                            {p.location && <Inf icon={MapPin} l="Location" v={p.location} />}
                        </div>
                        {p.summary && <p className="text-[11px] leading-relaxed text-muted-foreground rounded-lg bg-muted/30 px-2.5 py-2">{p.summary}</p>}
                    </div>

                    {/* Experience */}
                    {p.experience?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-2.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-primary" />Experience</h3>
                            <div className="relative space-y-3 pl-4 before:absolute before:left-[7px] before:top-1.5 before:h-[calc(100%-12px)] before:w-px before:bg-border">
                                {p.experience.map((e, i) => (
                                    <div key={i} className="relative">
                                        <div className="absolute -left-[13px] top-1 h-2 w-2 rounded-full border-2 border-primary bg-card" />
                                        <p className="text-xs font-semibold text-foreground">{e.title}</p>
                                        <p className="text-[11px] text-muted-foreground">{e.company}{e.duration ? ` · ${e.duration}` : ''}</p>
                                        {e.description && <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{e.description}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Education */}
                    {p.education?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-2.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-primary" />Education</h3>
                            {p.education.map((e, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10"><GraduationCap className="h-3.5 w-3.5 text-primary" /></div>
                                    <div>
                                        <p className="text-xs font-semibold text-foreground">{e.degree}</p>
                                        <p className="text-[11px] text-muted-foreground">{e.institution}{e.year ? ` · ${e.year}` : ''}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right col */}
                <div className="space-y-3">
                    {skillGroups.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-2.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-primary" />Skills</h3>
                            {skillGroups.map(g => (
                                <div key={g.label} className="space-y-1">
                                    {skillGroups.length > 1 && (
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">{g.label}</p>
                                    )}
                                    <div className="flex flex-wrap gap-1">
                                        {g.items.map(s => <span key={s} className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-foreground">{s}</span>)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {p.languages?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Languages className="h-3.5 w-3.5 text-primary" />Languages</h3>
                            <div className="flex flex-wrap gap-1">
                                {p.languages.map(l => <span key={l} className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-foreground">{l}</span>)}
                            </div>
                        </div>
                    )}
                    {p.certifications?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-primary" />Certifications</h3>
                            {p.certifications.map(c => (
                                <div key={c} className="flex items-start gap-1.5 text-[11px] text-foreground">
                                    <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5 text-primary" />{c}
                                </div>
                            ))}
                        </div>
                    )}
                    {(an.strengths?.length > 0 || an.weaknesses?.length > 0) && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-primary" />AI Analysis</h3>
                            {an.overallAssessment && <p className="text-[11px] leading-relaxed text-muted-foreground rounded-lg bg-muted/30 px-2.5 py-2">{an.overallAssessment}</p>}
                            {an.strengths?.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Strengths</p>
                                    {an.strengths.map((s, i) => <div key={i} className="flex items-start gap-1.5 text-[11px] text-foreground"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-500" />{s}</div>)}
                                </div>
                            )}
                            {an.weaknesses?.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />To Improve</p>
                                    {an.weaknesses.map((w, i) => <div key={i} className="flex items-start gap-1.5 text-[11px] text-foreground"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />{w}</div>)}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Inf({ icon: Icon, l, v }) {
    return (
        <div className="flex items-start gap-1.5">
            <Icon className="h-3 w-3 shrink-0 mt-0.5 text-muted-foreground" />
            <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</p>
                <p className="truncate text-[11px] font-medium text-foreground">{v}</p>
            </div>
        </div>
    );
}

/* ── Shared helpers for monitoring UI ───────────────────────────────────── */
function sigStatus(val, goodBelow, badAbove) {
    // goodBelow: val <= this → good; badAbove: val >= this → bad
    if (val <= goodBelow) return 'good';
    if (val >= badAbove)  return 'bad';
    return 'warning';
}
function sigStatusAbove(val, goodAbove, badBelow) {
    // goodAbove: val >= this → good; badBelow: val <= this → bad
    if (val >= goodAbove) return 'good';
    if (val <= badBelow)  return 'bad';
    return 'warning';
}
const STATUS_RING = {
    good:    'border-emerald-500/20 bg-emerald-500/5',
    warning: 'border-amber-500/20  bg-amber-500/5',
    bad:     'border-red-500/25    bg-red-500/5',
    neutral: 'border-border        bg-card/40',
};
const STATUS_FG = {
    good:    'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600   dark:text-amber-400',
    bad:     'text-red-600     dark:text-red-400',
    neutral: 'text-foreground',
};
const STATUS_DOT = {
    good:    'bg-emerald-500',
    warning: 'bg-amber-500',
    bad:     'bg-red-500',
    neutral: 'bg-muted-foreground/40',
};
const STATUS_LABEL = {
    good:    'Good',
    warning: 'Concern',
    bad:     'Problem',
    neutral: '—',
};

/** Single monitoring signal row */
function SignalRow({ Icon, label, value, status = 'neutral', what, tip }) {
    return (
        <div className={cn('flex items-start gap-3 rounded-lg border px-3 py-2.5', STATUS_RING[status])}>
            <div className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md', STATUS_RING[status])}>
                <Icon className={cn('h-3.5 w-3.5', STATUS_FG[status])} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-foreground">{label}</p>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={cn('text-xs font-black tabular-nums', STATUS_FG[status])}>{value}</span>
                        <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', STATUS_DOT[status])} />
                        <span className={cn('text-[10px] font-bold uppercase', STATUS_FG[status])}>{STATUS_LABEL[status]}</span>
                    </div>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{what}</p>
                {tip && <p className="mt-1 text-[10px] text-muted-foreground/70 italic leading-snug">⤷ {tip}</p>}
            </div>
        </div>
    );
}

/** Section header inside monitoring tab */
function MonSection({ icon: Icon, title, children }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{title}</p>
            </div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

/* ── IntegrityScoreCard ──────────────────────────────────────────────────── */
function IntegrityScoreCard({ monitoring, session }) {
    const { score, breakdown, computed } = resolveIntegrityDisplay(monitoring);
    if (score == null && session?.status !== 'terminated' && !hasMonitoringData(monitoring)) return null;

    // For terminated sessions the score is estimated from signal scalars (tabSwitchCount,
    // gazeOnScreenPct, etc.) but those fields are 0 / null when the session ended abruptly
    // before they could be persisted — giving a misleadingly perfect 100/100.
    // Cap the displayed score at 40 ("Poor") whenever the score is estimated and the
    // session was terminated, so the badge always reflects the proctoring violation.
    const displayScore = (session?.status === 'terminated' && computed)
        ? Math.min(score ?? 0, 40)
        : score;
    const ds = displayScore ?? 0;
    const scoreColor = ds >= 80 ? STATUS_FG.good : ds >= 60 ? STATUS_FG.warning : STATUS_FG.bad;
    const barColor   = ds >= 80 ? 'bg-emerald-500' : ds >= 60 ? 'bg-amber-500' : 'bg-red-500';
    const borderBg   = ds >= 80 ? STATUS_RING.good  : ds >= 60 ? STATUS_RING.warning  : STATUS_RING.bad;
    const label      = ds >= 85 ? 'Excellent' : ds >= 70 ? 'Good' : ds >= 50 ? 'Concerning' : 'Poor';
    const badgeCls   = ds >= 70
        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
        : ds >= 50
        ? 'bg-amber-500/10  text-amber-700  dark:text-amber-400  border-amber-500/20'
        : 'bg-red-500/10    text-red-700    dark:text-red-400    border-red-500/20';

    const m = monitoring;
    const gazeAwayPct  = m.gazeOnScreenPct != null ? Math.round(100 - m.gazeOnScreenPct) : 0;
    const noiseEvents  = (m.voiceEvents || []).filter(e => e.type === 'high_noise').length;
    const silenceEvents = (m.voiceEvents || []).filter(e => e.type === 'prolonged_silence').length;
    const multiLabel   = m.multipleFacesIncidents != null
        ? `${m.multipleFacesIncidents} incident${m.multipleFacesIncidents !== 1 ? 's' : ''}`
        : `${m.multipleFacesCount} frames`;

    const rows = [
        { Icon: WifiOff,    label: 'Tab switching',         raw: `×${m.tabSwitchCount ?? 0}`,         ded: breakdown?.tabSwitchDeduction     ?? 0, note: 'Terminates on 2nd switch'          },
        { Icon: Users,      label: 'Multiple faces',         raw: multiLabel,                          ded: breakdown?.multipleFacesDeduction ?? 0, note: 'Others in frame — report only'     },
        { Icon: ScanFace,   label: 'Lip-sync flags',         raw: `${m.lipSyncFlags ?? 0}`,            ded: breakdown?.lipSyncDeduction       ?? 0, note: 'Audio on, mouth not moving'        },
        { Icon: Eye,        label: 'Sideways gaze',          raw: `${gazeAwayPct}% away`,              ded: breakdown?.sidewaysGazeDeduction  ?? 0, note: 'Looking off-screen — report only'  },
        { Icon: AudioLines, label: 'Dual audio / coaching',  raw: `${m.dualAudioFlags ?? 0}`,          ded: breakdown?.dualAudioDeduction     ?? 0, note: 'Second voice detected — report only' },
    ];

    return (
        <div className={cn('rounded-xl border p-4 space-y-4', borderBg)}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5" />Interview Integrity Score
                </h2>
                <div className="flex items-center gap-2">
                    {displayScore !== null && (
                        <>
                            <span className={cn('text-3xl font-black tabular-nums', scoreColor)}>{ds}</span>
                            <span className="text-sm text-muted-foreground">/100</span>
                        </>
                    )}
                    <span className={cn('rounded-full border px-2.5 py-0.5 text-[10px] font-bold', badgeCls)}>
                        {displayScore === null ? 'No data' : label}
                    </span>
                    {computed && displayScore !== null && (
                        <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground">
                            Estimated
                        </span>
                    )}
                    {session?.status === 'terminated' && (
                        <span className="rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-400">
                            TERMINATED
                        </span>
                    )}
                </div>
            </div>

            {/* Score bar */}
            {displayScore !== null && (
                <div className="h-2 w-full rounded-full bg-muted/40 overflow-hidden">
                    <div className={cn('h-full rounded-full transition-all duration-1000', barColor)} style={{ width: `${ds}%` }} />
                </div>
            )}

            {/* 5 quick stats */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[
                    { v: `${m.gazeOnScreenPct ?? '—'}%`, l: 'Gaze on Screen',   bad: (m.gazeOnScreenPct ?? 100) < 70 },
                    { v: `${m.tabSwitchCount ?? 0}×`,    l: 'Tab Switches',      bad: (m.tabSwitchCount ?? 0) > 0 },
                    { v: `${m.noFaceTotalSecs ?? 0}s`,   l: 'Away from Camera',  bad: (m.noFaceTotalSecs ?? 0) > 20 },
                    { v: `${noiseEvents}`,                l: 'Noise Events',      bad: noiseEvents > 2 },
                    { v: `${m.lipSyncFlags ?? 0}`,        l: 'Lip-sync Flags',    bad: (m.lipSyncFlags ?? 0) > 0 },
                ].map(({ v, l, bad }) => (
                    <div key={l} className={cn('rounded-lg border px-2 py-1.5 text-center',
                        bad ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/20 bg-emerald-500/5')}>
                        <p className={cn('text-base font-black tabular-nums',
                            bad ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400')}>{v}</p>
                        <p className="text-[9px] font-medium text-muted-foreground leading-tight mt-0.5">{l}</p>
                    </div>
                ))}
            </div>

            {/* Deduction breakdown — always show signal rows; deductions show 0 if breakdown not yet computed */}
            <div className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Signal breakdown — started at 100
                </p>
                <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                    {rows.map(({ Icon, label, raw, ded, note }) => (
                        <div key={label} className="flex items-center gap-2.5 bg-background/50 px-3 py-2">
                            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-foreground truncate">{label}</p>
                                <p className="text-[10px] text-muted-foreground/60">{note}</p>
                            </div>
                            <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{raw}</span>
                            <span className={cn('w-9 text-right text-xs font-black tabular-nums shrink-0',
                                ded > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
                                {ded > 0 ? `−${ded}` : '+0'}
                            </span>
                        </div>
                    ))}
                    <div className="flex items-center justify-between bg-muted/30 px-3 py-2">
                        <span className="text-xs font-bold text-foreground">Final integrity score</span>
                        <span className={cn('text-sm font-black tabular-nums', scoreColor)}>
                            {displayScore !== null ? `${ds} / 100` : '—'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Termination reason */}
            {session?.status === 'terminated' && session?.terminationReason && (
                <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
                    <div>
                        <p className="text-xs font-bold text-red-700 dark:text-red-400">Session terminated</p>
                        <p className="text-[11px] text-red-600 dark:text-red-400 leading-snug">{session.terminationReason}</p>
                    </div>
                </div>
            )}

            <p className="flex items-center gap-1 text-[10px] text-muted-foreground/60">
                <Info className="h-3 w-3 shrink-0" />
                Full signal breakdown is available in the <span className="font-semibold">Monitoring</span> tab below.
            </p>
        </div>
    );
}

/* ── MonitoringTab ───────────────────────────────────────────────────────── */
function MonitoringTab({ monitoring, session, responses, totalResponseCount }) {
    const m = monitoring;
    const s = session || {};
    const { score: integrityScore, computed: integrityComputed } = resolveIntegrityDisplay(m);

    // ── Derived values ─────────────────────────────────────────────────────
    const gazeOn      = m.gazeOnScreenPct   ?? null;
    const gazeAway    = gazeOn != null ? Math.round(100 - gazeOn) : null;
    const noFaceSecs  = m.noFaceTotalSecs   ?? 0;
    const camCovered  = m.cameraCoveredCount ?? 0;
    const multiFaces  = m.multipleFacesCount ?? 0;
    const multiIncidents = m.multipleFacesIncidents ?? null;
    const lipSync     = m.lipSyncFlags       ?? 0;
    const dualAudio   = m.dualAudioFlags     ?? 0;
    const tabSwitch   = m.tabSwitchCount     ?? 0;
    const lookAway    = m.lookingAwayPct     ?? null;
    const noiseEvs    = (m.voiceEvents || []).filter(e => e.type === 'high_noise');
    const silenceEvs  = (m.voiceEvents || []).filter(e => e.type === 'prolonged_silence');
    const allVoiceEvs = m.voiceEvents || [];
    const infractions = m.proctoringInfractions || [];
    const eventLog    = m.proctoringEventLog || [];
    const warningCount = m.warningCount ?? 0;

    // Session timing
    const startedAt   = s.startedAt   ? new Date(s.startedAt)   : null;
    const completedAt = s.completedAt ? new Date(s.completedAt) : null;
    const durationMs  = startedAt && completedAt ? completedAt - startedAt : null;
    const durationMin = durationMs ? Math.round(durationMs / 60000) : null;

    const totalQs   = (s.questions || []).length;
    // Use totalResponseCount (from pagination.totalRecords in parent) so the count reflects
    // ALL submitted responses, not just the ones on the current page.
    const answered  = totalResponseCount ?? responses.length;

    // Per-question time
    const perQ = (s.questions || []).map((q, i) => {
        const resp = responses.find(r => r.question?.toString() === (q._id?.toString() || ''));
        const taken   = resp?.duration ?? null;
        const allowed = q.timeLimitSeconds ?? 300;
        const pct     = taken != null ? Math.min(100, Math.round((taken / allowed) * 100)) : null;
        return { idx: i, text: q.questionText, type: q.questionType, difficulty: q.difficulty,
                 taken, allowed, pct, score: resp?.aiScore ?? null };
    });

    const fmtSecs = (s) => s == null ? '—' : `${Math.floor(s / 60)}m ${s % 60}s`;

    if (!hasMonitoringData(m) && s.status !== 'terminated') {
        return (
            <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 text-center">
                <p className="text-sm text-muted-foreground">No monitoring data recorded for this session yet.</p>
                <p className="text-xs text-muted-foreground/80">Data is captured during the live interview (camera, gaze, audio, tab focus) and saved when the candidate finishes.</p>
            </div>
        );
    }

    const joinImages = m.joinImages || [];
    const questionImages = [...(m.questionImages || [])]
        .sort((a, b) => (a.questionIndex ?? 0) - (b.questionIndex ?? 0));

    return (
        <div className="space-y-5">

            {!m.fromReport && hasMonitoringData(m) && (
                <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" />
                    <p>
                        Showing live session monitoring data stored on the interview record.
                        {integrityComputed ? ' Integrity score is estimated from these signals until a report is generated.' : ''}
                    </p>
                </div>
            )}

            {/* ── 0. Candidate Verification Photos ───────────────────── */}
            {joinImages.length > 0 && (
                <MonSection icon={Camera} title="Candidate Verification Photos">
                    <p className="mb-3 text-xs text-muted-foreground leading-relaxed">
                        These photos were automatically captured in the first 15 seconds of the interview.
                        Use them to verify the candidate's identity against their profile.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {joinImages.map((url, i) => (
                            <a key={i} href={`${API_CONFIG.STATIC_BASE_URL}${url}`} target="_blank" rel="noopener noreferrer"
                                className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-colors">
                                <img
                                    src={`${API_CONFIG.STATIC_BASE_URL}${url}`}
                                    alt={`Capture ${i + 1}`}
                                    className="h-36 w-auto object-cover"
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1 text-center">
                                    <span className="text-[10px] font-semibold text-white">Photo {i + 1}</span>
                                </div>
                            </a>
                        ))}
                    </div>
                </MonSection>
            )}

            {/* ── 0b. Per-Question Snapshots ──────────────────────────── */}
            {questionImages.length > 0 && (
                <MonSection icon={Camera} title="Per-Question Snapshots">
                    <p className="mb-3 text-xs text-muted-foreground leading-relaxed">
                        One snapshot captured automatically ~2 seconds after each question was displayed.
                        Use these to spot any environment changes between questions.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {questionImages.map((img, i) => (
                            <a key={i} href={`${API_CONFIG.STATIC_BASE_URL}${img.imageUrl}`} target="_blank" rel="noopener noreferrer"
                                className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-colors">
                                <img
                                    src={`${API_CONFIG.STATIC_BASE_URL}${img.imageUrl}`}
                                    alt={`Q${(img.questionIndex ?? i) + 1} snapshot`}
                                    className="h-36 w-auto object-cover"
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1 text-center">
                                    <span className="text-[10px] font-semibold text-white">Q{(img.questionIndex ?? i) + 1}</span>
                                </div>
                            </a>
                        ))}
                    </div>
                </MonSection>
            )}

            {/* ── A. Session Overview ─────────────────────────────────── */}
            <MonSection icon={Activity} title="Session Overview">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                    {[
                        { label: 'Duration',       value: durationMin != null ? `${durationMin} min` : '—', sub: startedAt ? startedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' → ' + (completedAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? '?') : '—' },
                        { label: 'Questions',      value: `${answered}/${totalQs}`, sub: answered < totalQs ? `${totalQs - answered} skipped or terminated` : 'All answered' },
                        { label: 'Session Status', value: s.status === 'completed' ? 'Completed' : s.status === 'terminated' ? 'Terminated' : s.status ?? '—', sub: s.status === 'terminated' ? (s.terminationReason || 'Proctoring violation') : 'Normal completion', bad: s.status === 'terminated' },
                        { label: 'Integrity Score', value: integrityScore != null ? `${integrityScore}/100${integrityComputed ? ' (est.)' : ''}` : '—', sub: integrityScore >= 80 ? 'Excellent' : integrityScore >= 60 ? 'Concerning' : integrityScore != null ? 'Poor integrity' : 'Not calculated', bad: integrityScore != null && integrityScore < 70 },
                        { label: 'Warnings', value: `${warningCount}`, sub: warningCount > 0 ? 'Proctoring warnings issued' : 'No warnings', bad: warningCount > 0 },
                    ].map(({ label, value, sub, bad }) => (
                        <div key={label} className={cn('rounded-lg border px-3 py-2.5 space-y-0.5', bad ? 'border-red-500/25 bg-red-500/5' : 'border-border bg-card/60')}>
                            <p className={cn('text-sm font-black tabular-nums', bad ? 'text-red-600 dark:text-red-400' : 'text-foreground')}>{value}</p>
                            <p className="text-[10px] font-semibold text-muted-foreground">{label}</p>
                            <p className="text-[10px] text-muted-foreground/60 leading-tight">{sub}</p>
                        </div>
                    ))}
                </div>
            </MonSection>

            {/* ── B. Face & Presence ──────────────────────────────────── */}
            <MonSection icon={Camera} title="Face & Presence">
                <div className="grid gap-2 sm:grid-cols-2">
                    <SignalRow
                        Icon={ScanFace}
                        label="Face detection rate"
                        value={gazeOn != null ? `${Math.round(gazeOn)}% of frames` : '—'}
                        status={gazeOn == null ? 'neutral' : gazeOn >= 85 ? 'good' : gazeOn >= 65 ? 'warning' : 'bad'}
                        what="Percentage of frames where the candidate's face was clearly visible."
                        tip={gazeOn != null && gazeOn < 85 ? 'Candidate was frequently absent from camera. Check the session video.' : undefined}
                    />
                    <SignalRow
                        Icon={Clock}
                        label="Away from camera (total)"
                        value={noFaceSecs > 0 ? `${noFaceSecs}s` : 'None'}
                        status={sigStatus(noFaceSecs, 5, 40)}
                        what="Total seconds the camera detected no face."
                        tip={noFaceSecs > 20 ? 'Significant time away. May indicate candidate left their seat.' : undefined}
                    />
                    <SignalRow
                        Icon={Camera}
                        label="Camera covered / darkness"
                        value={camCovered > 0 ? `${camCovered} frames` : 'None detected'}
                        status={camCovered === 0 ? 'good' : 'bad'}
                        what="Frames where camera appeared covered or pitch dark (camera blocked)."
                        tip={camCovered > 0 ? 'Candidate may have covered the camera intentionally.' : undefined}
                    />
                    <SignalRow
                        Icon={Users}
                        label="Multiple faces detected"
                        value={multiIncidents != null && multiIncidents > 0
                            ? `${multiIncidents} incident${multiIncidents !== 1 ? 's' : ''} (${multiFaces} frames)`
                            : multiFaces > 0 ? `${multiFaces} frames` : 'None'}
                        status={multiFaces === 0 && (multiIncidents ?? 0) === 0 ? 'good' : (multiIncidents ?? multiFaces) <= 2 ? 'warning' : 'bad'}
                        what="Frames where more than one person's face was detected in the camera."
                        tip={multiFaces > 0 ? 'Another person may have been present. Tracked in report only — does not terminate.' : undefined}
                    />
                </div>
            </MonSection>

            {/* ── C. Gaze & Attention ─────────────────────────────────── */}
            <MonSection icon={Eye} title="Gaze & Attention">
                <div className="grid gap-2 sm:grid-cols-2">
                    <SignalRow
                        Icon={Eye}
                        label="On-screen gaze"
                        value={gazeOn != null ? `${gazeOn}%` : '—'}
                        status={gazeOn == null ? 'neutral' : sigStatusAbove(gazeOn, 80, 60)}
                        what="Percentage of time the candidate's eyes were directed at the screen (camera-facing)."
                        tip={gazeOn != null && gazeOn < 70 ? 'Low on-screen gaze may indicate reading external notes or looking away frequently.' : undefined}
                    />
                    <SignalRow
                        Icon={Eye}
                        label="Looking sideways"
                        value={lookAway != null ? `${lookAway}%` : gazeAway != null ? `${gazeAway}% (estimated)` : '—'}
                        status={sigStatus(lookAway ?? gazeAway ?? 0, 10, 30)}
                        what="Frames where the candidate's face was frontal but eyes were looking left or right."
                        tip={(lookAway ?? gazeAway ?? 0) > 20 ? 'Frequent sideways gaze may suggest referencing external materials.' : undefined}
                    />
                    <SignalRow
                        Icon={ScanFace}
                        label="Lip-sync flags"
                        value={lipSync > 0 ? `${lipSync} flags` : 'None'}
                        status={lipSync === 0 ? 'good' : lipSync <= 2 ? 'warning' : 'bad'}
                        what="Detected when audio was clearly active but candidate's mouth showed no visible movement — may indicate pre-recorded audio playback."
                        tip={lipSync > 0 ? 'Investigate further. Could be technical artifact or pre-recorded response.' : undefined}
                    />
                    <SignalRow
                        Icon={Target}
                        label="Focus consistency"
                        value={gazeOn != null ? (gazeOn >= 80 ? 'High' : gazeOn >= 60 ? 'Moderate' : 'Low') : '—'}
                        status={gazeOn == null ? 'neutral' : gazeOn >= 80 ? 'good' : gazeOn >= 60 ? 'warning' : 'bad'}
                        what="Overall assessment of how consistently the candidate maintained camera focus."
                    />
                </div>
            </MonSection>

            {/* ── D. Audio & Voice ────────────────────────────────────── */}
            <MonSection icon={AudioLines} title="Audio & Voice Analysis">
                <div className="grid gap-2 sm:grid-cols-2">
                    <SignalRow
                        Icon={Volume2}
                        label="High noise events"
                        value={noiseEvs.length > 0 ? `${noiseEvs.length} event${noiseEvs.length !== 1 ? 's' : ''}` : 'None'}
                        status={noiseEvs.length === 0 ? 'good' : noiseEvs.length <= 2 ? 'warning' : 'bad'}
                        what="Sustained loud background noise (>70% volume for >4 seconds) detected during the interview."
                        tip={noiseEvs.length > 0 ? `Occurred at: ${noiseEvs.slice(0, 3).map(e => new Date(e.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })).join(', ')}` : undefined}
                    />
                    <SignalRow
                        Icon={Mic}
                        label="Prolonged silence events"
                        value={silenceEvs.length > 0 ? `${silenceEvs.length} event${silenceEvs.length !== 1 ? 's' : ''}` : 'None'}
                        status={silenceEvs.length <= 1 ? 'neutral' : silenceEvs.length <= 3 ? 'warning' : 'bad'}
                        what="Extended periods (>6 seconds) of complete silence while a question was active."
                        tip={silenceEvs.length > 2 ? 'Frequent silences may indicate difficulty with questions or distraction.' : undefined}
                    />
                    <SignalRow
                        Icon={AudioLines}
                        label="Dual voice / coaching"
                        value={dualAudio > 0 ? `${dualAudio} detections` : 'None detected'}
                        status={dualAudio === 0 ? 'good' : dualAudio <= 3 ? 'warning' : 'bad'}
                        what="Detected when two distinct voice frequencies were simultaneously present — may indicate a second person coaching the candidate."
                        tip={dualAudio > 0 ? 'Correlate with timestamps from high-noise events for confirmation.' : undefined}
                    />
                    <SignalRow
                        Icon={Activity}
                        label="Voice consistency"
                        value={silenceEvs.length === 0 && noiseEvs.length <= 1 ? 'Normal' : 'Irregular'}
                        status={silenceEvs.length === 0 && noiseEvs.length <= 1 ? 'good' : 'warning'}
                        what="Overall assessment of audio consistency throughout the interview session."
                    />
                </div>
            </MonSection>

            {/* ── E. Behavioral Flags ─────────────────────────────────── */}
            <MonSection icon={MousePointerClick} title="Behavioral Flags">
                <div className="grid gap-2 sm:grid-cols-2">
                    <SignalRow
                        Icon={WifiOff}
                        label="Tab / window switching"
                        value={tabSwitch === 0 ? 'None' : `${tabSwitch} switch${tabSwitch !== 1 ? 'es' : ''}`}
                        status={tabSwitch === 0 ? 'good' : tabSwitch === 1 ? 'warning' : 'bad'}
                        what="Number of times the candidate switched to a different browser tab or application window."
                        tip={tabSwitch >= 2 ? 'Session was terminated due to repeated tab switching.' : tabSwitch === 1 ? 'Warning was shown to candidate. One more switch would terminate the session.' : undefined}
                    />
                    <SignalRow
                        Icon={MonitorOff}
                        label="Fullscreen compliance"
                        value={s.status === 'terminated' && s.terminationReason?.includes('fullscreen') ? 'Violated' : 'Compliant'}
                        status={s.terminationReason?.includes('fullscreen') ? 'bad' : 'good'}
                        what="Whether the candidate remained in fullscreen mode for the duration of the interview."
                    />
                    <SignalRow
                        Icon={ShieldCheck}
                        label="Overall behavior"
                        value={tabSwitch === 0 && lipSync === 0 && dualAudio === 0 ? 'Clean' : 'Flags present'}
                        status={tabSwitch === 0 && lipSync === 0 && dualAudio === 0 ? 'good' : tabSwitch >= 2 ? 'bad' : 'warning'}
                        what="Summary of whether any suspicious behavioral patterns were detected during the session."
                    />
                    <SignalRow
                        Icon={Timer}
                        label="Session completion"
                        value={s.status === 'completed' ? 'Full interview' : s.status === 'terminated' ? 'Terminated early' : s.status ?? '—'}
                        status={s.status === 'completed' ? 'good' : 'bad'}
                        what="Whether the candidate completed all questions or if the session ended prematurely."
                        tip={s.status === 'terminated' ? s.terminationReason || 'Check termination reason above.' : undefined}
                    />
                </div>
            </MonSection>

            {/* ── F. Proctoring warnings & infractions ─────────────────── */}
            {(warningCount > 0 || infractions.length > 0) && (
                <MonSection icon={ShieldAlert} title="Proctoring Warnings & Infractions">
                    <div className="grid gap-2 sm:grid-cols-2">
                        <SignalRow
                            Icon={ShieldAlert}
                            label="Warning count"
                            value={warningCount > 0 ? `${warningCount}` : 'None'}
                            status={warningCount === 0 ? 'good' : warningCount <= 2 ? 'warning' : 'bad'}
                            what="Number of proctoring warnings shown to the candidate during the interview."
                        />
                        <SignalRow
                            Icon={Activity}
                            label="Logged infractions"
                            value={infractions.length > 0 ? `${infractions.length} recorded` : 'None'}
                            status={infractions.length === 0 ? 'good' : 'warning'}
                            what="Distinct infraction events sent from the interview client when rules were violated."
                        />
                    </div>
                    {infractions.length > 0 && (
                        <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                            {infractions.map((inf, i) => (
                                <li key={i} className="flex items-start gap-2 bg-background/50 px-3 py-2 text-[11px]">
                                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                                    <span className="text-foreground leading-snug">{formatProctoringEntry(inf)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </MonSection>
            )}

            {/* ── G. Proctoring event log (periodic snapshots) ─────────── */}
            {eventLog.length > 0 && (
                <MonSection icon={Activity} title="Proctoring Event Log">
                    <p className="mb-2 text-xs text-muted-foreground leading-relaxed">
                        Periodic snapshots from the proctor service during the interview ({eventLog.length} entries).
                    </p>
                    <div className="max-h-64 overflow-y-auto divide-y divide-border rounded-lg border border-border">
                        {eventLog.map((ev, i) => (
                            <div key={i} className="grid gap-1 bg-background/50 px-3 py-2 sm:grid-cols-[9rem_1fr]">
                                <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">{formatEventTime(ev)}</span>
                                <span className="text-[11px] text-foreground leading-snug">{formatProctoringEntry(ev)}</span>
                            </div>
                        ))}
                    </div>
                </MonSection>
            )}

            {/* ── H. All voice / audio events ─────────────────────────── */}
            {allVoiceEvs.length > 0 && (
                <MonSection icon={Volume2} title="Audio Event Log">
                    <p className="mb-2 text-xs text-muted-foreground leading-relaxed">
                        Microphone monitoring events captured client-side ({allVoiceEvs.length} total).
                    </p>
                    <div className="max-h-48 overflow-y-auto divide-y divide-border rounded-lg border border-border">
                        <div className="grid grid-cols-12 bg-muted/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground sticky top-0">
                            <span className="col-span-4">Time</span>
                            <span className="col-span-4">Type</span>
                            <span className="col-span-4 text-right">Detail</span>
                        </div>
                        {allVoiceEvs.map((ev, i) => (
                            <div key={i} className="grid grid-cols-12 items-center gap-1 px-3 py-1.5 text-[11px] bg-background/50">
                                <span className="col-span-4 tabular-nums text-muted-foreground">
                                    {ev.time ? new Date(ev.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                                </span>
                                <span className="col-span-4 font-medium capitalize text-foreground">{(ev.type || 'event').replace(/_/g, ' ')}</span>
                                <span className="col-span-4 text-right text-muted-foreground tabular-nums">
                                    {ev.level != null ? `level ${Math.round(ev.level)}` : ev.durationMs != null ? `${ev.durationMs}ms` : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </MonSection>
            )}

            {/* ── I. Per-Question Time Analysis ───────────────────────── */}
            {perQ.length > 0 && (
                <MonSection icon={Timer} title="Per-Question Time Analysis">
                    <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-12 bg-muted/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            <span className="col-span-1">#</span>
                            <span className="col-span-5">Question</span>
                            <span className="col-span-2 text-right">Time taken</span>
                            <span className="col-span-2 text-right">Allowed</span>
                            <span className="col-span-2 text-right">AI score</span>
                        </div>
                        {perQ.map(({ idx, text, type, difficulty, taken, allowed, pct, score }) => {
                            const timeStatus = taken == null ? 'neutral'
                                : pct <= 20 ? 'warning'   // very fast — may not have engaged
                                : pct >= 95 ? 'warning'   // hit the limit
                                : 'good';
                            const scoreStatus = score == null ? 'neutral'
                                : score >= 70 ? 'good'
                                : score >= 45 ? 'warning'
                                : 'bad';
                            return (
                                <div key={idx} className="grid grid-cols-12 items-center gap-1 px-3 py-2 bg-background/50 hover:bg-muted/20 transition-colors">
                                    <span className="col-span-1 text-[11px] font-bold text-muted-foreground">Q{idx + 1}</span>
                                    <div className="col-span-5 min-w-0 space-y-0.5">
                                        <p className="text-[11px] font-medium text-foreground line-clamp-1">{text}</p>
                                        <div className="flex items-center gap-1 flex-wrap">
                                            {type && <span className="rounded-full bg-primary/10 px-1.5 py-px text-[9px] font-semibold text-primary capitalize">{type}</span>}
                                            {difficulty && <span className={cn('rounded-full px-1.5 py-px text-[9px] font-semibold capitalize',
                                                difficulty === 'easy' ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                                : difficulty === 'hard' ? 'bg-red-500/10 text-red-700 dark:text-red-400'
                                                : 'bg-amber-500/10 text-amber-700 dark:text-amber-400')}>{difficulty}</span>}
                                        </div>
                                        {/* Time bar */}
                                        {pct != null && (
                                            <div className="h-1 w-full max-w-[80%] rounded-full bg-muted/40 overflow-hidden mt-1">
                                                <div className={cn('h-full rounded-full', timeStatus === 'good' ? 'bg-emerald-500' : 'bg-amber-500')}
                                                    style={{ width: `${pct}%` }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span className={cn('text-[11px] font-bold tabular-nums', STATUS_FG[timeStatus])}>
                                            {fmtSecs(taken)}
                                        </span>
                                        {pct != null && <p className="text-[9px] text-muted-foreground">{pct}% used</p>}
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span className="text-[11px] text-muted-foreground tabular-nums">{fmtSecs(allowed)}</span>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        {score != null
                                            ? <span className={cn('text-sm font-black tabular-nums', STATUS_FG[scoreStatus])}>{Math.round(score)}</span>
                                            : <span className="text-[11px] text-muted-foreground">—</span>
                                        }
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Summary row */}
                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2 text-[11px]">
                        <span className="font-semibold text-foreground">Summary:</span>
                        <span className="text-muted-foreground">
                            Avg score: <span className="font-bold text-foreground">
                                {perQ.filter(q => q.score != null).length > 0
                                    ? Math.round(perQ.filter(q => q.score != null).reduce((a, q) => a + q.score, 0) / perQ.filter(q => q.score != null).length)
                                    : '—'}
                            </span>
                        </span>
                        <span className="text-muted-foreground">
                            Avg time used: <span className="font-bold text-foreground">
                                {perQ.filter(q => q.pct != null).length > 0
                                    ? Math.round(perQ.filter(q => q.pct != null).reduce((a, q) => a + q.pct, 0) / perQ.filter(q => q.pct != null).length)
                                    : '—'}%
                            </span>
                        </span>
                        <span className="text-muted-foreground">
                            Questions answered: <span className="font-bold text-foreground">{answered}/{totalQs}</span>
                        </span>
                    </div>
                </MonSection>
            )}

            {/* ── What was measured disclaimer ────────────────────────── */}
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 space-y-2">
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    <Info className="h-3 w-3" />What was measured during this interview
                </p>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 text-[11px] text-muted-foreground">
                    {[
                        ['Face presence', 'Camera frame-by-frame analysis via OpenCV'],
                        ['Gaze direction', 'Eye-centre asymmetry within detected face region'],
                        ['Lip-sync detection', 'Mouth ROI pixel variance vs audio level'],
                        ['Dual voice / coaching', 'FFT dual-frequency peak analysis (80–300 Hz)'],
                        ['Tab switching', 'Browser visibility API — exact count'],
                        ['Audio events', 'Real-time microphone level monitoring'],
                        ['Multiple faces', 'Frontal face cascade count per frame'],
                        ['Camera coverage', 'Frame brightness mean < 25 threshold'],
                        ['AI answer scoring', 'Claude 3 — relevance, completeness, keywords'],
                        ['Time management', 'Per-question timer vs actual response duration'],
                    ].map(([metric, method]) => (
                        <div key={metric} className="flex items-start gap-1.5">
                            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary/50" />
                            <span><span className="font-semibold text-foreground">{metric}</span> — {method}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ── skeleton ───────────────────────────────────────────────────────────── */
function DetailedReportSkeleton() {
    return (
        <div className="space-y-4">
            {/* Top bar */}
            <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-8 w-32 rounded-md" />
                <Skeleton className="h-8 w-28 rounded-md" />
            </div>

            {/* Hero card — 3 columns */}
            <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">
                    {/* Col 1 — candidate info */}
                    <div className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
                            <div className="space-y-1.5 flex-1">
                                <Skeleton className="h-3.5 w-32" />
                                <Skeleton className="h-3 w-40" />
                            </div>
                        </div>
                        <Skeleton className="h-3 w-36" />
                        <Skeleton className="h-3 w-28" />
                        <div className="flex gap-2">
                            <Skeleton className="h-5 w-20 rounded-full" />
                            <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                    </div>
                    {/* Col 2 — score */}
                    <div className="p-4 flex flex-col items-center justify-center gap-3">
                        <Skeleton className="h-20 w-20 rounded-full" />
                        <Skeleton className="h-3.5 w-24" />
                        <Skeleton className="h-3 w-32" />
                    </div>
                    {/* Col 3 — proctoring / stats */}
                    <div className="p-4 space-y-3">
                        <Skeleton className="h-3.5 w-28" />
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="flex items-center justify-between">
                                <Skeleton className="h-3 w-28" />
                                <Skeleton className="h-3 w-10" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tabs bar */}
            <div className="flex gap-2">
                {[80, 60, 80, 90].map((w, i) => (
                    <Skeleton key={i} className="h-8 rounded-md" style={{ width: w }} />
                ))}
            </div>

            {/* Content area — question cards */}
            <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-3">
                        <div className="flex items-start gap-3">
                            <Skeleton className="h-6 w-6 rounded-full shrink-0 mt-0.5" />
                            <div className="flex-1 space-y-1.5">
                                <Skeleton className="h-3.5 w-full" />
                                <Skeleton className="h-3.5 w-5/6" />
                            </div>
                            <Skeleton className="h-6 w-12 rounded-full shrink-0" />
                        </div>
                        <Skeleton className="h-px w-full" />
                        <div className="space-y-1.5 pl-9">
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-4/5" />
                            <Skeleton className="h-3 w-3/4" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── main ───────────────────────────────────────────────────────────────── */
export default function DetailedReportPage({ sessionId: sidProp, onBack }) {
    const { sessionId: sidRoute } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const sid = sidProp || sidRoute;

    const isAdminRoute = location.pathname.startsWith('/admin');
    // If opened from the pipeline (via Link state), go back to that process
    const fromProcessId = location.state?.processId;
    const goBack = onBack || (() => {
        if (fromProcessId) return navigate(`/admin/candidate-process/${fromProcessId}`);
        navigate(isAdminRoute ? '/admin/reports' : '/dashboard');
    });

    const [tab, setTab] = useState('questions');
    const [report, setReport] = useState(null);
    const [session, setSession] = useState(null);
    const [responses, setResponses] = useState([]);
    const [pages, setPages] = useState({ page: 1, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [exporting, setExporting] = useState(false);
    const [reportGenerating, setReportGenerating] = useState(false);
    const [pollReportActive, setPollReportActive] = useState(false);
    const [reportPollTimedOut, setReportPollTimedOut] = useState(false);
    const [regenFromAudioLoading, setRegenFromAudioLoading] = useState(false);
    const [regenFromAudioInfo, setRegenFromAudioInfo] = useState(null);
    const pollRef = useRef(null);
    const pollStartedRef = useRef(0);

    const fetchReportStatus = useCallback(async () => {
        const rr = await apiClient.getReport(sid);
        const { report: nextReport, generating, done } = parseReportFetchResult(rr);
        setReport(nextReport);
        if (pollReportActive) {
            setReportGenerating(generating);
            if (done) {
                setPollReportActive(false);
                setReportGenerating(false);
            }
        }
        if (done) setReportPollTimedOut(false);
        return done;
    }, [sid, pollReportActive]);

    const load = useCallback(async (pg = 1) => {
        setLoading(true); setErr('');
        setReportPollTimedOut(false);
        setPollReportActive(false);
        setReportGenerating(false);
        try {
            const [sr, resp] = await Promise.all([
                apiClient.getSessionDetailsForAdmin(sid),
                apiClient.getSessionResponses(sid, pg),
            ]);
            setSession(sr.data);
            setResponses(resp.responses); setPages(resp.pagination);

            try {
                await fetchReportStatus();
            } catch {
                setReport(null);
                setReportGenerating(false);
            }
        } catch (e) { setErr(e.message || 'Failed to load.'); }
        finally { setLoading(false); }
    }, [sid, fetchReportStatus]);

    useEffect(() => { load(1); }, [sid]);

    // Re-fetch responses when the user navigates to a different page via Prev/Next.
    // Without this effect, clicking pagination only changed the page number label
    // but never fetched the actual data for that page.
    const prevPageRef = useRef(1);
    useEffect(() => {
        if (pages.page !== prevPageRef.current) {
            prevPageRef.current = pages.page;
            load(pages.page);
        }
    }, [pages.page, load]);

    useEffect(() => {
        if (!pollReportActive || reportPollTimedOut) return undefined;

        pollStartedRef.current = Date.now();
        pollRef.current = setInterval(async () => {
            try {
                const done = await fetchReportStatus();
                if (shouldStopReportPolling({ done, startedAt: pollStartedRef.current })) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    if (!done) {
                        setReportPollTimedOut(true);
                        setPollReportActive(false);
                        setReportGenerating(false);
                    }
                }
            } catch (err) {
                console.warn('[DetailedReportPage] Report poll failed:', err?.message || err);
                if (shouldStopReportPolling({ done: false, startedAt: pollStartedRef.current })) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setReportPollTimedOut(true);
                    setPollReportActive(false);
                    setReportGenerating(false);
                }
            }
        }, 5000);

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [pollReportActive, reportPollTimedOut, fetchReportStatus]);

    const exportPdf = () => {
        setExporting(true);
        apiClient.exportReport(sid, 'pdf').catch(e => toast.error(e.message || 'Export failed.')).finally(() => setExporting(false));
    };

    const startGenerateReport = async () => {
        const ok = window.confirm(
            'Generate report now?\n\nAI will score answers and create the report. This may take 30–90 seconds.'
        );
        if (!ok) return;
        setPollReportActive(true);
        setReportGenerating(true);
        setReportPollTimedOut(false);
        try {
            await apiClient.generateReport(sid, {});
        } catch (e) {
            toast.error(e.message || 'Failed to start report generation.');
            setPollReportActive(false);
            setReportGenerating(false);
        }
    };

    const regenerateFromAudio = async () => {
        const ok = window.confirm(
            'Regenerate report from saved audio?\n\nThis will re-transcribe answers and may change the candidate score/recommendation.'
        );
        if (!ok) return;
        setRegenFromAudioLoading(true);
        setRegenFromAudioInfo(null);
        try {
            const rr = await apiClient.regenerateReportFromAudio(sid);
            if (rr?.retranscribe) setRegenFromAudioInfo(rr.retranscribe);
            setPollReportActive(true);
            setReport(null);
            setReportGenerating(true);
            setReportPollTimedOut(false);
        } catch (e) {
            toast.error(e.message || 'Failed to regenerate from audio.');
        } finally {
            setRegenFromAudioLoading(false);
        }
    };

    if (loading) return <DetailedReportSkeleton />;
    if (err) return (
        <div className="flex h-52 flex-col items-center justify-center gap-3">
            <p className="text-sm text-destructive">{err}</p>
            <Button variant="outline" size="sm" onClick={() => load(1)}>Retry</Button>
        </div>
    );

    const { skillScores, skillsDistribution, detailedAnalysis, interviewSummary, feedback, recommendations, overallScore, aiRecommendation } = report || {};
    const radar = skillsDistribution ? Object.entries(skillsDistribution).map(([skill, score]) => ({ skill, score })) : [];
    const cand = session?.candidate;
    const monitoring = mergeMonitoringData(report, session);
    const tmpl = session?.template;
    const doneAt = session?.completedAt;
    const decStatus = session?.decision?.status;
    const pfd = session?.processFinalDecision;
    const approved  = decStatus === 'approved' || pfd === 'hire';
    const rejected_ = decStatus === 'rejected' || pfd === 'reject';
    const finalized  = approved || rejected_;
    const overall = Math.round(Number(overallScore) || 0);
    const emptyTranscriptCount = (responses || []).filter(r => {
        const t = (r?.transcribedText ?? '').toString().trim();
        return !t || t === '[No speech detected]' || t === '[No response recorded]';
    }).length;
    const missingAudioCount = (responses || []).filter(r => {
        // Prefer explicit backend upload marker when available
        if (r?.audioUploadedAt) return false;
        const u = (r?.audioFileUrl ?? '').toString().trim();
        return !u || u === 'N/A';
    }).length;
    const scoringMissingCount = (responses || []).filter(r => r?.aiScore == null).length;
    const likelyDataIssue = !!report && session?.status === 'completed' && responses?.length > 0 && overall === 0;

    return (
        <div className="space-y-4">
            {/* top bar */}
            <div className="flex items-center justify-between gap-3">
                <Button variant="ghost" size="sm" className="gap-1.5 px-0 hover:px-2" onClick={goBack}>
                    <ArrowLeft className="h-4 w-4" />Back to Reports
                </Button>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={regenFromAudioLoading || reportGenerating}
                        onClick={regenerateFromAudio}
                        className="gap-1.5"
                        title="Re-transcribe saved audio and regenerate the report"
                    >
                        {regenFromAudioLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                        {regenFromAudioLoading ? 'Regenerating…' : 'Regenerate from audio'}
                    </Button>
                    <Button size="sm" variant="outline" disabled={exporting || !report} onClick={exportPdf} className="gap-1.5">
                        {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                        {exporting ? 'Exporting…' : 'Export PDF'}
                    </Button>
                </div>
            </div>

            {regenFromAudioInfo && (
                <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
                    Re-transcribe stats — updated: <span className="font-bold text-foreground">{regenFromAudioInfo.updated}</span>, skipped: <span className="font-bold text-foreground">{regenFromAudioInfo.skipped}</span>, errors: <span className="font-bold text-foreground">{regenFromAudioInfo.errors}</span>
                </div>
            )}

            {/* Report generating — only after explicit Generate / Regenerate from audio */}
            {pollReportActive && reportGenerating && !report && (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-4 text-sm">
                    <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
                    <div>
                        <p className="font-medium text-foreground">Report is being generated…</p>
                        <p className="text-muted-foreground">AI is scoring responses and building the report. This usually takes 30–90 seconds.</p>
                    </div>
                </div>
            )}

            {reportPollTimedOut && !report && (
                <div className="flex flex-col gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                    <p>Report generation is taking longer than expected. Try refreshing in a few minutes.</p>
                    <Button variant="outline" size="sm" className="w-fit" onClick={() => { setReportPollTimedOut(false); load(1); }}>
                        Retry
                    </Button>
                </div>
            )}

            {/* Explicit CTA when report is missing (do not auto-generate on page load) */}
            {!report && !reportGenerating && !reportPollTimedOut && session?.status === 'completed' && (responses?.length || 0) > 0 && (
                <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-sm">
                    <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                        <div className="min-w-0">
                            <p className="font-medium text-foreground">Report not generated yet</p>
                            <p className="text-muted-foreground text-xs">
                                To avoid changing results automatically on page load, report generation starts only when you click.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" className="w-fit" onClick={startGenerateReport}>
                            Generate report
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={regenFromAudioLoading}
                            onClick={regenerateFromAudio}
                            className="gap-1.5 w-fit"
                            title="Re-transcribe saved audio and regenerate the report"
                        >
                            {regenFromAudioLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                            {regenFromAudioLoading ? 'Regenerating…' : 'Regenerate from audio'}
                        </Button>
                    </div>
                </div>
            )}

            {/* No-report notice for in-progress / terminated sessions */}
            {!report && !reportGenerating && !reportPollTimedOut && session && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {session.status === 'terminated'
                        ? 'This interview was terminated before completion — no AI report is available.'
                        : session.status === 'in_progress'
                            ? 'This interview is still in progress — the report will be available once completed.'
                            : 'No AI report is available for this session.'}
                </div>
            )}

            {/* Diagnostics for 0-score / missing media */}
            {(likelyDataIssue || session?.reportGenerationError) && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 space-y-2">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-amber-700 dark:text-amber-300">
                                Report quality warning
                            </p>
                            <p className="text-xs text-amber-700/80 dark:text-amber-300/80 leading-relaxed">
                                This usually happens when the browser did not capture a transcript (Safari), the microphone was muted/wrong input,
                                or the audio evidence upload failed. You can regenerate from saved audio to recover transcripts and scoring.
                            </p>
                        </div>
                    </div>

                    {session?.reportGenerationError && (
                        <div className="rounded-lg border border-amber-500/30 bg-black/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                            <span className="font-bold">Last generation error:</span> {session.reportGenerationError}
                        </div>
                    )}

                    <div className="grid gap-2 sm:grid-cols-3">
                        <div className="rounded-lg border border-border/60 bg-background/40 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Empty transcripts</p>
                            <p className="text-sm font-black text-foreground tabular-nums">{emptyTranscriptCount}/{responses.length || 0}</p>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-background/40 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Missing audio</p>
                            <p className="text-sm font-black text-foreground tabular-nums">{missingAudioCount}/{responses.length || 0}</p>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-background/40 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Unscored answers</p>
                            <p className="text-sm font-black text-foreground tabular-nums">{scoringMissingCount}/{responses.length || 0}</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={regenFromAudioLoading || reportGenerating}
                            onClick={regenerateFromAudio}
                            className="gap-1.5"
                        >
                            {regenFromAudioLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                            {regenFromAudioLoading ? 'Regenerating…' : 'Regenerate from audio'}
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={pollReportActive && reportGenerating}
                            onClick={async () => {
                                setReportPollTimedOut(false);
                                setPollReportActive(true);
                                setReportGenerating(true);
                                try {
                                    const done = await fetchReportStatus();
                                    if (done) {
                                        setPollReportActive(false);
                                        setReportGenerating(false);
                                    }
                                } catch {
                                    setPollReportActive(false);
                                    setReportGenerating(false);
                                }
                            }}
                        >
                            Re-check report status
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Single unified hero card ───────────────────────────── */}
            <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">

                    {/* Col 1 — Candidate info */}
                    <div className="relative overflow-hidden p-4">
                        <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-primary/5 pointer-events-none" />
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-base font-black text-primary ring-2 ring-primary/20">
                                {(cand?.firstName?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-sm font-bold text-foreground leading-snug">{cand?.firstName} {cand?.lastName}</h1>
                                <p className="truncate text-[11px] text-muted-foreground">{cand?.email || '—'}</p>
                            </div>
                        </div>
                        <div className="space-y-1.5 mb-3">
                            {tmpl?.title && (
                                <div className="flex items-center gap-1.5 text-[11px]">
                                    <Briefcase className="h-3 w-3 shrink-0 text-primary/60" />
                                    <span className="truncate font-medium text-foreground">{tmpl.title}</span>
                                </div>
                            )}
                            {doneAt && (
                                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                    <Calendar className="h-3 w-3 shrink-0 text-primary/60" />
                                    <span>{new Date(doneAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                    <span className="text-muted-foreground/50">·</span>
                                    <span>{new Date(doneAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                                </div>
                            )}
                        </div>
                        {/* AI recommendation + final decision status (read-only) */}
                        <div className="flex flex-wrap gap-1.5">
                            {aiRecommendation && <RecoBadge value={aiRecommendation} />}
                            {finalized && (
                                <span className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                                    approved ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25'
                                             : 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/25')}>
                                    {approved ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                    {approved ? 'Hired' : 'Rejected'}
                                </span>
                            )}
                        </div>
                        {/* Termination banner */}
                        {session?.status === 'terminated' && (
                            <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 space-y-1">
                                <div className="flex items-center gap-1.5">
                                    <ShieldAlert className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0" />
                                    <span className="text-xs font-bold text-red-700 dark:text-red-400">Interview Terminated</span>
                                    {session.warningCount > 0 && (
                                        <span className="ml-auto text-[10px] text-red-600 dark:text-red-400 tabular-nums">{session.warningCount} infraction{session.warningCount !== 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                {session.terminationReason && (
                                    <p className="text-[11px] text-red-600 dark:text-red-400 leading-snug">{session.terminationReason}</p>
                                )}
                                {session.proctoringInfractions?.length > 0 && (
                                    <ul className="space-y-0.5 mt-1">
                                        {session.proctoringInfractions.map((inf, i) => (
                                            <li key={i} className="text-[10px] text-red-500 dark:text-red-400 flex items-start gap-1">
                                                <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                                                {typeof inf === 'string' ? inf : inf.type || inf.reason || JSON.stringify(inf)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Col 2 — Overall Score */}
                    <div className="flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-primary/[0.03] to-transparent p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Overall Score</p>
                        <Donut score={overall} />
                        <p className="text-[10px] text-muted-foreground">
                            {responses.length > 0 ? `${responses.length} responses evaluated` : 'AI evaluated'}
                        </p>
                    </div>

                    {/* Col 3 — Skill Breakdown */}
                    <div className="p-4">
                        <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Skill Breakdown</p>
                        <div className="space-y-2">
                            <Metric Icon={Brain}         label="Technical"       score={skillScores?.technical || 0} />
                            <Metric Icon={MessageSquare} label="Communication"   score={skillScores?.communication || 0} />
                            <Metric Icon={Award}         label="Behavioral"      score={skillScores?.behavioral || 0} />
                            <Metric Icon={Puzzle}        label="Problem Solving" score={skillScores?.problemSolving || 0} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Always-visible analysis row — 3 columns ───────────── */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {/* Skill Analysis */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        <TrendingUp className="h-3.5 w-3.5 text-primary" />Skill Analysis
                    </h2>
                    {(detailedAnalysis || []).length > 0
                        ? <div className="space-y-3">{(detailedAnalysis || []).map((item, i) => <SkillBar key={i} skill={item.skill} score={item.score} description={item.description} />)}</div>
                        : <p className="text-xs text-muted-foreground">No detailed analysis available.</p>
                    }
                </div>

                {/* Skill Distribution */}
                <div className="rounded-xl border border-border bg-card p-4">
                    <h2 className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        <BarChart3 className="h-3.5 w-3.5 text-primary" />Skill Distribution
                    </h2>
                    <SkillsRadarChart data={radar} />
                </div>

                {/* Summary + Recommendations */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3 flex flex-col">
                    <div className="space-y-1.5">
                        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            <FileText className="h-3.5 w-3.5 text-primary" />Summary
                        </h2>
                        <ExpandableText
                            text={interviewSummary || '—'}
                            className="text-xs leading-relaxed text-foreground"
                            clampLines={4}
                        />
                        {feedback && (
                            <div className="rounded-lg border-l-2 border-primary bg-muted/30 px-2.5 py-1.5">
                                <ExpandableText
                                    text={feedback}
                                    className="text-[11px] italic leading-relaxed text-muted-foreground"
                                    clampLines={3}
                                />
                            </div>
                        )}
                    </div>
                    <div className="space-y-1.5 border-t border-border pt-3 flex-1">
                        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            <Star className="h-3.5 w-3.5 text-primary" />AI Recommendations
                        </h2>
                        {aiRecommendation && (
                            <div className="flex items-center gap-1.5">
                                <RecoBadge value={aiRecommendation} />
                                <span className="text-[10px] text-muted-foreground">AI verdict</span>
                            </div>
                        )}
                        {recommendations && (
                            <div className="rounded-lg border border-border bg-muted/20 p-2.5">
                                <ExpandableText
                                    text={recommendations}
                                    className="text-[11px] leading-relaxed text-foreground"
                                    clampLines={4}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Integrity Score Card ───────────────────────────────── */}
            <IntegrityScoreCard monitoring={monitoring} session={session} />

            {/* ── Tabs: Q&A · Responses · Monitoring · Resume ─────── */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-4 h-9">
                    <TabsTrigger value="questions"  className="gap-1.5 text-xs"><FileText  className="h-3.5 w-3.5" />Q&amp;A</TabsTrigger>
                    <TabsTrigger value="breakdown"  className="gap-1.5 text-xs"><Mic       className="h-3.5 w-3.5" />Responses</TabsTrigger>
                    <TabsTrigger value="monitoring" className="gap-1.5 text-xs">
                        <Activity className="h-3.5 w-3.5" />Monitoring
                        {hasMonitoringData(monitoring) && (
                            <span className="rounded-full bg-primary/15 px-1.5 py-px text-[9px] font-bold text-primary">Live</span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="resume"     className="gap-1.5 text-xs"><User      className="h-3.5 w-3.5" />Resume</TabsTrigger>
                </TabsList>

                {/* Q&A */}
                <TabsContent value="questions" className="mt-3">
                    {!session?.questions?.length ? (
                        <div className="flex h-36 items-center justify-center rounded-xl border border-dashed border-border">
                            <p className="text-sm text-muted-foreground">No questions available.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">{session.questions.length} questions — tap to expand</p>
                            {session.questions.map((q, i) => <QCard key={q._id || i} q={q} i={i} />)}
                        </div>
                    )}
                </TabsContent>

                {/* Responses */}
                <TabsContent value="breakdown" className="mt-3 space-y-3">
                    {!responses.length ? (
                        <div className="flex h-36 items-center justify-center rounded-xl border border-dashed border-border">
                            <p className="text-sm text-muted-foreground">No responses recorded.</p>
                        </div>
                    ) : (
                        <>
                            <p className="text-xs text-muted-foreground">Page {pages.page}/{pages.totalPages} · {responses.length} shown</p>
                            <div className="grid gap-3 sm:grid-cols-2">{responses.map((r, i) => <ResCard key={r._id} res={r} i={i} sessionId={sid} />)}</div>
                            {pages.totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 pt-1">
                                    <Button variant="outline" size="sm" disabled={pages.page <= 1} onClick={() => setPages(p => ({ ...p, page: p.page - 1 }))}>Prev</Button>
                                    <span className="text-xs text-muted-foreground">{pages.page}/{pages.totalPages}</span>
                                    <Button variant="outline" size="sm" disabled={pages.page >= pages.totalPages} onClick={() => setPages(p => ({ ...p, page: p.page + 1 }))}>Next</Button>
                                </div>
                            )}
                        </>
                    )}
                </TabsContent>

                {/* Monitoring */}
                <TabsContent value="monitoring" className="mt-3">
                    <MonitoringTab monitoring={monitoring} session={session} responses={responses} totalResponseCount={pages.totalRecords} />
                </TabsContent>

                {/* Resume */}
                <TabsContent value="resume" className="mt-3">
                    <ResumeTab candidateId={cand?._id} />
                </TabsContent>
            </Tabs>

        </div>
    );
}
